// NEW FILE: StaffRatingItem.jsx
// This component handles individual staff/service ratings for each receipt item

import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';

const StaffRatingItem = ({
    invoiceDetailId,
    staffName,
    staffId,
    serviceName,
    existingRating,
    primaryColor,
    secondaryColor,
    primaryHighlightColor,
    secondaryHighlightColor,
    isAlreadyRated = false
}) => {
    const [rating, setRating] = useState(0);
    const [selectedStar, setSelectedStar] = useState(0);
    const [isRated, setIsRated] = useState(isAlreadyRated);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Initialize rating if already rated
    useEffect(() => {
        if (existingRating && existingRating > 0) {
            setRating(existingRating);
            setSelectedStar(existingRating);
            setIsRated(true);
        }
    }, [existingRating]);

    // Handle star click - auto-submit instantly
    const handleStarClick = (value) => {
        // Prevent rating if already rated
        if (isRated) return;
        
        // Prevent multiple rapid submissions
        if (isSubmitting) return;

        // Update UI immediately for better UX
        setSelectedStar(value);
        setRating(value);
        setIsSubmitting(true);

        // Prepare payload for API
        const payload = {
            invoice_detail_id: invoiceDetailId,
            staff_name: staffName,
            staff_id: staffId,
            rating: value,
            service_name: serviceName
        };

        // Auto-submit via AJAX
        fetch(`${process.env.REACT_APP_BASE_URL}/submit-service-rating`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
        })
        .then(response => response.json())
        .then(data => {
            setIsSubmitting(false);
            if (data.success) {
                setIsRated(true);
                // Show a subtle success toast
                Swal.fire({
                    icon: 'success',
                    title: 'Rating Submitted!',
                    text: `You rated ${staffName || serviceName} ${value} stars`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 2000,
                    timerProgressBar: true,
                });
            } else {
                // Revert rating on error
                setSelectedStar(rating);
                setRating(rating);
                Swal.fire({
                    icon: 'error',
                    title: 'Submission Failed',
                    text: data.message || 'Please try again later',
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000,
                });
            }
        })
        .catch(error => {
            console.error('Error submitting staff rating:', error);
            setIsSubmitting(false);
            // Revert rating on error
            setSelectedStar(rating);
            setRating(rating);
            Swal.fire({
                icon: 'error',
                title: 'Network Error',
                text: 'Please check your connection and try again',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000,
            });
        });
    };

    // ============================================
    // CLEAN STYLES - NO BACKGROUND, NO BORDER, NO BOX
    // Using color logic similar to StarRating component
    // ============================================
    
    const containerStyle = {
        borderBottom: `0.1px solid #666`,
        padding: '12px 0',
    };

    // Same font style for both staff name and Rank me Now
    const nameTextStyle = {
        fontWeight: 'bold',
        fontSize: '16px',
        color: '#666',
    };

    const nameTextStyle2 = {
        fontWeight: 'bold',
        fontSize: '13px',
        color: '#666',
    };

    const starsContainerStyle = {
        display: 'flex',
        gap: '6px',
        alignItems: 'center',
    };

    // Star style following your StarRating component pattern
    const starStyle = (isFilled) => ({
        fontSize: '26px',
        cursor: isRated ? 'default' : 'pointer',
        color: isFilled ? (secondaryColor || '#ffc107') : '#ddd',
        transition: 'transform 0.2s ease, color 0.2s ease',
        display: 'inline-block',
    });

    // Hover effect using CSS class instead of inline styles
    const starHoverClass = !isRated ? 'staff-star-hover' : '';

    const ratedBadgeStyle = {
        fontSize: '11px',
        color: '#28a745',
        marginLeft: '12px',
    };

    return (
        <div className="staff-rating-item" style={containerStyle}>
            {/* Staff Name - Single line */}
            <div>
                <span style={nameTextStyle}>
                    {staffName || serviceName || 'Staff Member'}
                </span>
            </div>
            {/* Stars - Under Staff Name */}
            <div style={starsContainerStyle}>
               <span style={nameTextStyle2}>
                   Rank me :
                </span> {[1, 2, 3, 4, 5].map((starValue) => (
                    <span
                        key={starValue}
                        className={`staff-star ${starHoverClass} ${starValue <= selectedStar ? 'staff-star-filled' : 'staff-star-empty'}`}
                        style={starStyle(starValue <= selectedStar)}
                        onClick={() => handleStarClick(starValue)}
                        onMouseEnter={(e) => {
                            if (!isRated && !isSubmitting) {
                                e.currentTarget.style.transform = 'scale(1.2)';
                                e.currentTarget.style.color = secondaryColor || '#ffc107';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!isRated) {
                                e.currentTarget.style.transform = 'scale(1)';
                                if (!(starValue <= selectedStar)) {
                                    e.currentTarget.style.color = '#ddd';
                                } else {
                                    e.currentTarget.style.color = secondaryColor || '#ffc107';
                                }
                            }
                        }}
                        role="button"
                        aria-label={`Rate ${starValue} stars`}
                    >
                        ★
                    </span>
                ))}
            </div>
            <style jsx>{`
                .staff-star {
                    transition: transform 0.2s ease, color 0.2s ease;
                }
                .staff-star-hover:hover {
                    transform: scale(1.2);
                }
            `}</style>
        </div>
    );
};

export default StaffRatingItem;